// Onboarding Guide (PRD §4.1). Checklist: connect → verify → create template
// → import subscribers → first broadcast. Each step deep-links; completion is
// persisted to localStorage (mock until backend progress lands).
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../lib/i18n';
import { cn } from '../lib/cn';
import { GUIDE_STEPS, GUIDE_SEED_DONE, GUIDE_STORAGE_KEY } from '../mocks/onboarding';

function loadDone(): Set<string> {
  try {
    const raw = localStorage.getItem(GUIDE_STORAGE_KEY);
    if (raw) return new Set(JSON.parse(raw) as string[]);
  } catch {
    /* ignore malformed storage */
  }
  return new Set(GUIDE_SEED_DONE);
}

export default function OnboardingGuide() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [done, setDone] = useState<Set<string>>(loadDone);

  function toggle(key: string) {
    setDone((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        localStorage.setItem(GUIDE_STORAGE_KEY, JSON.stringify([...next]));
      } catch {
        /* ignore */
      }
      return next;
    });
  }

  const total = GUIDE_STEPS.length;
  const completed = GUIDE_STEPS.filter((s) => done.has(s.key)).length;
  const pct = Math.round((completed / total) * 100);
  const allDone = completed === total;

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="text-[22px] font-extrabold text-ink">{t('guide.title')}</h1>
      <p className="mb-5 text-ink-3">{t('guide.subtitle')}</p>

      {/* Progress */}
      <div className="mb-5 rounded-card border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-2 flex items-center justify-between text-sm font-bold text-ink">
          <span>
            {allDone ? `🎉 ${t('guide.allDone')}` : `${completed}/${total} ${t('guide.complete')}`}
          </span>
          <span className="text-primary">{pct}%</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-slate-200">
          <i className="block h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Steps */}
      <ol className="space-y-3">
        {GUIDE_STEPS.map((step, i) => {
          const isDone = done.has(step.key);
          return (
            <li
              key={step.key}
              className={cn(
                'flex items-center gap-3 rounded-card border bg-white p-4 shadow-sm',
                isDone ? 'border-primary/40' : 'border-slate-200',
              )}
            >
              <button
                type="button"
                onClick={() => toggle(step.key)}
                role="checkbox"
                aria-checked={isDone}
                aria-label={t(`guide.${step.key}.title`)}
                className={cn(
                  'grid h-8 w-8 flex-shrink-0 place-items-center rounded-full border-2 text-sm font-extrabold',
                  isDone
                    ? 'border-primary bg-primary text-white'
                    : 'border-slate-300 bg-white text-ink-3',
                )}
              >
                {isDone ? '✓' : i + 1}
              </button>

              <div className="min-w-0 flex-1">
                <b className={cn('block text-sm', isDone ? 'text-ink-3 line-through' : 'text-ink')}>
                  <span className="me-1.5" aria-hidden>
                    {step.icon}
                  </span>
                  {t(`guide.${step.key}.title`)}
                </b>
                <span className="text-xs text-ink-3">{t(`guide.${step.key}.desc`)}</span>
              </div>

              <button
                type="button"
                onClick={() => navigate(step.route)}
                className="flex-shrink-0 rounded-[9px] border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-primary"
              >
                {isDone ? t('guide.review') : t('guide.start')}
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
